import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  AlertCircle,
  ArrowRight,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
  Phone,
  ReceiptText,
  Send,
} from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { Section } from '@/components/Section'
import { LocalizedLink } from '@/components/LocalizedLink'
import { usePageSEO } from '@/hooks/usePageSEO'
import { cn } from '@/lib/utils'

/* ============================================================
   Geolocation constants — Fenix Srl headquarters in Rivalba (TO)
   Used to build the OpenStreetMap iframe and the Google Maps link.
   The exact pin may be refined later without touching the layout.
   ============================================================ */
const FENIX_COORDS = {
  lat: 45.1509,
  lng: 7.9308,
} as const

// OpenStreetMap embed — no API key required. We build a small bounding
// box around the coordinates so the map frames the headquarters clearly.
const OSM_BBOX = {
  west: FENIX_COORDS.lng - 0.012,
  east: FENIX_COORDS.lng + 0.012,
  south: FENIX_COORDS.lat - 0.008,
  north: FENIX_COORDS.lat + 0.008,
} as const

const OSM_EMBED_URL = `https://www.openstreetmap.org/export/embed.html?bbox=${OSM_BBOX.west}%2C${OSM_BBOX.south}%2C${OSM_BBOX.east}%2C${OSM_BBOX.north}&layer=mapnik&marker=${FENIX_COORDS.lat}%2C${FENIX_COORDS.lng}`

const GOOGLE_MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${FENIX_COORDS.lat}%2C${FENIX_COORDS.lng}`

/* ============================================================
   Form types
   ============================================================ */
interface FormState {
  name: string
  email: string
  phone: string
  company: string
  subject: string
  message: string
  privacy: boolean
}

type FieldKey = keyof Omit<FormState, 'privacy'> | 'privacy'

type FormErrors = Partial<Record<FieldKey, string>>

type SubmitStatus = 'idle' | 'submitting' | 'success'

const EMPTY_FORM: FormState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  subject: '',
  message: '',
  privacy: false,
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/* ============================================================
   ContactRecordCard — the engineered "technical record" tile on
   the left column. A white panel with a brand-blue top rule, a
   coordinate-tick header and icon+label rows for every contact
   data point. Visually matches the PartnersPage CountryBlock
   style so the page family feels consistent.
   ============================================================ */
function ContactRecordCard() {
  const { t } = useTranslation()

  const rows = [
    {
      icon: Building2,
      label: t('contacts.companyLabel'),
      value: t('contacts.companyName'),
      href: undefined,
    },
    {
      icon: MapPin,
      label: t('contacts.addressLabel'),
      value: `${t('contact.address')}, ${t('contact.city')}`,
      href: GOOGLE_MAPS_URL,
      external: true,
    },
    {
      icon: Phone,
      label: t('contacts.phoneLabel'),
      value: t('contact.phone'),
      href: `tel:${t('contact.phone').replace(/\s/g, '')}`,
    },
    {
      icon: Mail,
      label: t('contacts.emailLabel'),
      value: t('contact.email'),
      href: `mailto:${t('contact.email')}`,
    },
    {
      icon: Clock,
      label: t('contacts.hoursLabel'),
      value: t('contacts.hoursValue'),
      href: undefined,
    },
    {
      icon: ReceiptText,
      label: t('contacts.vatLabel'),
      value: t('contact.vat'),
      href: undefined,
    },
  ]

  return (
    <article
      aria-labelledby="contacts-record-title"
      className="relative isolate overflow-hidden bg-white border border-ink-200 shadow-sm"
    >
      {/* Top brand rule */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[3px] bg-brand-500"
      />

      {/* Position badge — tiny coordinate marker at top-right */}
      <span
        aria-hidden="true"
        className="absolute top-4 right-5 font-display font-bold text-[10px] tracking-[0.22em] uppercase text-ink-400"
      >
        {t('contacts.recordNumber')}
      </span>

      {/* Header */}
      <header className="px-6 pt-9 pb-7 md:px-8 md:pt-10 md:pb-8 border-b border-ink-200 bg-gradient-to-br from-white via-white to-ink-50">
        <p className="mb-2 inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.26em] text-brand-600">
          <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
          {t('contacts.contactsRecordEyebrow')}
        </p>
        <h2
          id="contacts-record-title"
          className="font-display font-bold text-2xl md:text-[1.75rem] leading-tight text-ink-900"
        >
          {t('contacts.contactsRecordTitle')}
        </h2>
      </header>

      {/* Rows */}
      <dl className="divide-y divide-ink-200">
        {rows.map((row, i) => {
          const Icon = row.icon
          const isLast = i === rows.length - 1
          const isExternal = Boolean(row.external)
          const body = (
            <div className="flex items-start gap-4 md:gap-5">
              {/* Icon tile */}
              <div
                aria-hidden="true"
                className={cn(
                  'relative shrink-0 flex h-10 w-10 items-center justify-center',
                  'bg-ink-900 text-white',
                  'shadow-[inset_0_-2px_0_0_var(--color-brand-500)]'
                )}
              >
                {/* Corner ticks */}
                <span className="absolute left-1 top-1 block h-2 w-px bg-brand-500" />
                <span className="absolute left-1 top-1 block h-px w-2 bg-brand-500" />
                <Icon className="h-4 w-4" strokeWidth={2.25} />
              </div>

              {/* Body */}
              <div className="flex-1 min-w-0 pt-0.5">
                <dt className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500">
                  {row.label}
                </dt>
                <dd
                  className={cn(
                    'mt-1.5 font-display font-semibold leading-snug text-ink-900 text-base md:text-[17px]',
                    row.href && 'transition-colors'
                  )}
                >
                  {row.href ? (
                    <a
                      href={row.href}
                      {...(isExternal
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                      className="group/link inline-flex items-start gap-2 hover:text-brand-600 focus:outline-none focus-visible:text-brand-600 focus-visible:underline underline-offset-4"
                    >
                      <span className="break-words">{row.value}</span>
                      {isExternal && (
                        <ArrowUpRight
                          className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-500 transition-transform duration-200 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
                          strokeWidth={2.75}
                          aria-hidden="true"
                        />
                      )}
                    </a>
                  ) : (
                    <span className="break-words">{row.value}</span>
                  )}
                </dd>
              </div>
            </div>
          )

          return (
            <div
              key={row.label}
              className={cn('px-6 py-5 md:px-8 md:py-6', isLast && 'pb-8')}
            >
              {body}
            </div>
          )
        })}
      </dl>
    </article>
  )
}

/* ============================================================
   FormField — a reusable engineered input with label above,
   required asterisk in brand-blue, sharp borders, focus-ring,
   and an error slot below. Used for text, email, tel inputs.
   ============================================================ */
interface FormFieldProps {
  id: string
  name: string
  type?: string
  label: string
  required?: boolean
  value: string
  onChange: (value: string) => void
  placeholder?: string
  autoComplete?: string
  error?: string
  inputMode?: 'text' | 'email' | 'tel'
}

function FormField({
  id,
  name,
  type = 'text',
  label,
  required = false,
  value,
  onChange,
  placeholder,
  autoComplete,
  error,
  inputMode,
}: FormFieldProps) {
  const { t } = useTranslation()
  const errorId = `${id}-error`

  return (
    <div className="relative">
      <label
        htmlFor={id}
        className="mb-2 flex items-baseline justify-between gap-2"
      >
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-700">
          {label}
          {required && (
            <span
              className="ml-1 text-brand-600"
              aria-hidden="true"
            >
              *
            </span>
          )}
        </span>
        {required && (
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-400">
            {t('contacts.form.requiredMark')}
          </span>
        )}
      </label>

      <div className="relative">
        {/* Left brand bar — appears on focus via :focus-within */}
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute left-0 top-0 bottom-0 w-[3px] transition-colors duration-150',
            error ? 'bg-red-500' : 'bg-transparent'
          )}
        />
        <input
          id={id}
          name={name}
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          aria-required={required}
          className={cn(
            'w-full rounded-sm bg-white px-4 py-3',
            'text-base text-ink-900 placeholder:text-ink-400',
            'border border-ink-300',
            'transition-[border-color,box-shadow] duration-150',
            'focus:outline-none',
            'focus:border-brand-500 focus:shadow-[inset_3px_0_0_0_var(--color-brand-500)]',
            error &&
              'border-red-500 focus:border-red-500 focus:shadow-[inset_3px_0_0_0_theme(colors.red.500)]'
          )}
        />
      </div>

      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-red-600"
        >
          <AlertCircle
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
            strokeWidth={2.5}
            aria-hidden="true"
          />
          <span>{error}</span>
        </p>
      )}
    </div>
  )
}

/* ============================================================
   TextAreaField — same visual contract as FormField but with
   a resizable textarea.
   ============================================================ */
interface TextAreaFieldProps {
  id: string
  name: string
  label: string
  required?: boolean
  value: string
  onChange: (value: string) => void
  placeholder?: string
  error?: string
  rows?: number
}

function TextAreaField({
  id,
  name,
  label,
  required = false,
  value,
  onChange,
  placeholder,
  error,
  rows = 5,
}: TextAreaFieldProps) {
  const { t } = useTranslation()
  const errorId = `${id}-error`

  return (
    <div className="relative">
      <label
        htmlFor={id}
        className="mb-2 flex items-baseline justify-between gap-2"
      >
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-700">
          {label}
          {required && (
            <span className="ml-1 text-brand-600" aria-hidden="true">
              *
            </span>
          )}
        </span>
        {required && (
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-400">
            {t('contacts.form.requiredMark')}
          </span>
        )}
      </label>

      <textarea
        id={id}
        name={name}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        aria-required={required}
        className={cn(
          'w-full rounded-sm bg-white px-4 py-3',
          'text-base text-ink-900 placeholder:text-ink-400',
          'border border-ink-300 resize-y min-h-[140px]',
          'transition-[border-color,box-shadow] duration-150',
          'focus:outline-none',
          'focus:border-brand-500 focus:shadow-[inset_3px_0_0_0_var(--color-brand-500)]',
          error &&
            'border-red-500 focus:border-red-500 focus:shadow-[inset_3px_0_0_0_theme(colors.red.500)]'
        )}
      />

      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-red-600"
        >
          <AlertCircle
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
            strokeWidth={2.5}
            aria-hidden="true"
          />
          <span>{error}</span>
        </p>
      )}
    </div>
  )
}

/* ============================================================
   PrivacyCheckbox — custom engineered consent control.
   Sharp square, brand-blue tick, accessible native <input>.
   ============================================================ */
interface PrivacyCheckboxProps {
  id: string
  checked: boolean
  onChange: (checked: boolean) => void
  error?: string
}

function PrivacyCheckbox({
  id,
  checked,
  onChange,
  error,
}: PrivacyCheckboxProps) {
  const errorId = `${id}-error`

  return (
    <div>
      <label
        htmlFor={id}
        className="group flex items-start gap-3.5 cursor-pointer select-none"
      >
        <span className="relative flex h-5 w-5 shrink-0 items-center justify-center mt-0.5">
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 rounded-sm border-2 transition-colors duration-150',
              'border-ink-400 bg-white',
              error && !checked && 'border-red-500'
            )}
            data-checkbox-frame
          />
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth={3}
            stroke="currentColor"
            className={cn(
              'pointer-events-none relative h-3.5 w-3.5 text-white transition-opacity duration-150',
              checked ? 'opacity-100' : 'opacity-0'
            )}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            aria-required={true}
            required
            className={cn(
              'peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0',
              'focus-visible:outline-none'
            )}
          />
          {/* Brand fill — sibling after the native input so the
              peer-checked selector matches. Kept non-interactive. */}
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 rounded-sm bg-brand-500 transition-opacity duration-150',
              checked ? 'opacity-100' : 'opacity-0'
            )}
          />
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth={3}
            stroke="currentColor"
            className={cn(
              'pointer-events-none absolute h-3.5 w-3.5 text-white transition-opacity duration-150',
              checked ? 'opacity-100' : 'opacity-0'
            )}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute -inset-1 rounded-sm ring-2 ring-brand-300 opacity-0 transition-opacity duration-150',
              'peer-focus-visible:opacity-100'
            )}
          />
        </span>

        <span className="text-sm leading-relaxed text-ink-700 fx-text-pretty">
          <Trans
            i18nKey="contacts.form.privacy"
            components={{
              1: (
                <LocalizedLink
                  to="privacy"
                  onClick={(e) => e.stopPropagation()}
                  className="font-semibold text-brand-600 underline underline-offset-2 decoration-brand-500/60 hover:text-brand-700 hover:decoration-brand-600"
                />
              ),
            }}
          />
          <span className="ml-1 text-brand-600" aria-hidden="true">
            *
          </span>
        </span>
      </label>

      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-red-600"
        >
          <AlertCircle
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
            strokeWidth={2.5}
            aria-hidden="true"
          />
          <span>{error}</span>
        </p>
      )}
    </div>
  )
}

/* ============================================================
   ContactForm — the right column.
   ============================================================ */
function ContactForm() {
  const { t } = useTranslation()
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [status, setStatus] = useState<SubmitStatus>('idle')
  const [submittedOnce, setSubmittedOnce] = useState(false)

  const hasErrors = Object.keys(errors).length > 0

  const updateField =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }))
      if (submittedOnce) {
        // Re-validate the specific field live after first submit
        setErrors((prev) => {
          const next = { ...prev }
          const validation = validateField(key, value, { ...form, [key]: value })
          if (validation) {
            next[key as FieldKey] = validation
          } else {
            delete next[key as FieldKey]
          }
          return next
        })
      }
    }

  const validateField = (
    key: keyof FormState,
    value: FormState[keyof FormState],
    _full: FormState
  ): string | undefined => {
    if (key === 'name') {
      return typeof value === 'string' && value.trim().length === 0
        ? t('contacts.form.required')
        : undefined
    }
    if (key === 'email') {
      if (typeof value !== 'string' || value.trim().length === 0) {
        return t('contacts.form.required')
      }
      if (!EMAIL_REGEX.test(value.trim())) {
        return t('contacts.form.emailInvalid')
      }
      return undefined
    }
    if (key === 'message') {
      return typeof value === 'string' && value.trim().length === 0
        ? t('contacts.form.required')
        : undefined
    }
    if (key === 'privacy') {
      return value === true ? undefined : t('contacts.form.privacyRequired')
    }
    return undefined
  }

  const validateAll = (state: FormState): FormErrors => {
    const next: FormErrors = {}
    const keys: Array<keyof FormState> = ['name', 'email', 'message', 'privacy']
    for (const key of keys) {
      const err = validateField(key, state[key], state)
      if (err) next[key as FieldKey] = err
    }
    return next
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmittedOnce(true)
    const nextErrors = validateAll(form)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      // Focus the first invalid field for accessibility
      const firstKey = Object.keys(nextErrors)[0]
      const el =
        typeof document !== 'undefined'
          ? (document.getElementById(`contact-${firstKey}`) as
              | HTMLInputElement
              | HTMLTextAreaElement
              | null)
          : null
      el?.focus()
      return
    }

    // Simulate async submit (client-side only for now)
    setStatus('submitting')
    window.setTimeout(() => {
      setStatus('success')
    }, 500)
  }

  const handleReset = () => {
    setForm(EMPTY_FORM)
    setErrors({})
    setStatus('idle')
    setSubmittedOnce(false)
  }

  if (status === 'success') {
    return (
      <SuccessPanel onReset={handleReset} />
    )
  }

  return (
    <article
      aria-labelledby="contact-form-title"
      className="relative isolate overflow-hidden bg-white border border-ink-200 shadow-sm"
    >
      {/* Top brand rule */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[3px] bg-brand-500"
      />

      {/* Corner coordinate ticks */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 h-5 w-[3px] bg-ink-900"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 flex flex-col items-end gap-1 p-4 opacity-60"
      >
        <span className="block h-px w-6 bg-brand-500" />
        <span className="block h-px w-3 bg-ink-300" />
      </span>

      {/* Header */}
      <header className="px-6 pt-9 pb-6 md:px-10 md:pt-10 md:pb-7 border-b border-ink-200 bg-gradient-to-br from-white via-white to-ink-50">
        <p className="mb-2 inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.26em] text-brand-600">
          <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
          {t('contacts.form.eyebrow')}
        </p>
        <h2
          id="contact-form-title"
          className="font-display font-bold text-2xl md:text-[1.75rem] leading-tight text-ink-900"
        >
          {t('contacts.form.title')}
        </h2>
        <p className="mt-3 max-w-2xl fx-text-pretty text-sm md:text-base leading-relaxed text-ink-600">
          {t('contacts.form.intro')}
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="px-6 py-8 md:px-10 md:py-10 space-y-6"
        aria-describedby={hasErrors ? 'contact-form-summary' : undefined}
      >
        {hasErrors && (
          <div
            id="contact-form-summary"
            role="alert"
            className="relative flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3 rounded-sm"
          >
            <AlertCircle
              className="mt-0.5 h-4 w-4 shrink-0 text-red-600"
              strokeWidth={2.5}
              aria-hidden="true"
            />
            <p className="text-sm font-semibold text-red-700">
              {t('contacts.form.validationHeading')}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <FormField
            id="contact-name"
            name="name"
            label={t('contacts.form.name')}
            required
            value={form.name}
            onChange={updateField('name')}
            placeholder={t('contacts.form.namePlaceholder')}
            autoComplete="name"
            error={errors.name}
          />
          <FormField
            id="contact-email"
            name="email"
            type="email"
            inputMode="email"
            label={t('contacts.form.email')}
            required
            value={form.email}
            onChange={updateField('email')}
            placeholder={t('contacts.form.emailPlaceholder')}
            autoComplete="email"
            error={errors.email}
          />
          <FormField
            id="contact-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            label={t('contacts.form.phone')}
            value={form.phone}
            onChange={updateField('phone')}
            placeholder={t('contacts.form.phonePlaceholder')}
            autoComplete="tel"
            error={errors.phone}
          />
          <FormField
            id="contact-company"
            name="company"
            label={t('contacts.form.company')}
            value={form.company}
            onChange={updateField('company')}
            autoComplete="organization"
            error={errors.company}
          />
        </div>

        <FormField
          id="contact-subject"
          name="subject"
          label={t('contacts.form.subject')}
          value={form.subject}
          onChange={updateField('subject')}
          error={errors.subject}
        />

        <TextAreaField
          id="contact-message"
          name="message"
          label={t('contacts.form.message')}
          required
          value={form.message}
          onChange={updateField('message')}
          placeholder={t('contacts.form.messagePlaceholder')}
          error={errors.message}
        />

        {/* Divider */}
        <div className="pt-2 border-t border-ink-200" aria-hidden="true" />

        <PrivacyCheckbox
          id="contact-privacy"
          checked={form.privacy}
          onChange={updateField('privacy')}
          error={errors.privacy}
        />

        {/* Submit row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
          <button
            type="submit"
            disabled={status === 'submitting'}
            className={cn(
              'group relative inline-flex items-center gap-3',
              'rounded-sm bg-brand-500 px-7 py-4',
              'text-sm md:text-base font-bold uppercase tracking-[0.08em] text-white',
              'shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)]',
              'transition-[background-color,transform,box-shadow] duration-200',
              'hover:bg-brand-400 hover:-translate-y-0.5',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
              'disabled:pointer-events-none disabled:opacity-60'
            )}
          >
            {/* Left tick accent */}
            <span
              aria-hidden="true"
              className="absolute -left-[3px] top-1/2 block h-6 w-[3px] -translate-y-1/2 bg-white/90 transition-all duration-200 group-hover:h-8"
            />
            {status === 'submitting'
              ? t('contacts.form.submitting')
              : t('contacts.form.submit')}
            <Send
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              strokeWidth={2.75}
            />
          </button>

          <p className="text-[11px] leading-relaxed text-ink-500 fx-text-pretty">
            <span className="font-bold uppercase tracking-[0.16em] text-brand-600">
              *
            </span>{' '}
            {t('contacts.form.required')}
          </p>
        </div>
      </form>
    </article>
  )
}

/* ============================================================
   SuccessPanel — replaces the form after a successful submit.
   ============================================================ */
function SuccessPanel({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation()
  return (
    <article
      aria-labelledby="contact-success-title"
      role="status"
      className="relative isolate overflow-hidden bg-white border border-ink-200 shadow-sm"
    >
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[3px] bg-brand-500"
      />
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 h-5 w-[3px] bg-ink-900"
      />

      <div className="px-6 py-12 md:px-10 md:py-16 text-center">
        <div
          aria-hidden="true"
          className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center bg-brand-500 text-white shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)]"
        >
          <CheckCircle2 className="h-8 w-8" strokeWidth={2} />
        </div>

        <p className="mb-3 inline-flex items-center justify-center gap-3 text-[10px] font-bold uppercase tracking-[0.26em] text-brand-600">
          <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
          {t('contacts.form.eyebrow')}
          <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
        </p>

        <h2
          id="contact-success-title"
          className="font-display font-bold text-2xl md:text-[1.75rem] leading-tight text-ink-900"
        >
          {t('contacts.form.successTitle')}
        </h2>

        <p className="mt-4 mx-auto max-w-xl text-base md:text-[17px] leading-relaxed text-ink-600 fx-text-pretty">
          {t('contacts.form.successBody')}
        </p>

        <button
          type="button"
          onClick={onReset}
          className={cn(
            'mt-8 inline-flex items-center gap-2',
            'rounded-sm border-2 border-brand-500 px-5 py-3',
            'text-xs md:text-sm font-bold uppercase tracking-[0.14em] text-brand-600',
            'transition-colors duration-200',
            'hover:bg-brand-500 hover:text-white',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2'
          )}
        >
          {t('contacts.form.resetCta')}
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.75} />
        </button>
      </div>
    </article>
  )
}

/* ============================================================
   MapBlock — the bottom "how to reach us" band. An OpenStreetMap
   iframe wrapped in a coordinate-framed container, with a small
   caption row and a Google Maps CTA for directions.
   ============================================================ */
function MapBlock() {
  const { t } = useTranslation()
  const address = `${t('contact.address')}, ${t('contact.city')}`

  return (
    <div className="relative">
      {/* Framed map container with corner ticks */}
      <div className="relative">
        <div
          aria-hidden="true"
          className="absolute -top-3 -left-3 h-14 w-14 border-t-[3px] border-l-[3px] border-brand-500"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-3 -right-3 h-14 w-14 border-b-[3px] border-r-[3px] border-brand-500"
        />

        <div className="relative overflow-hidden rounded-sm border border-ink-200 bg-ink-100 shadow-sm">
          <div className="aspect-[16/10] md:aspect-[21/9] w-full">
            <iframe
              title={t('contacts.mapTitle')}
              src={OSM_EMBED_URL}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-full w-full border-0 grayscale-[35%]"
            />
          </div>
        </div>
      </div>

      {/* Info strip below the map */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-12 items-start gap-4 md:gap-6">
        <div className="md:col-span-8 flex items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center bg-ink-900 text-white shadow-[inset_0_-2px_0_0_var(--color-brand-500)]"
          >
            <MapPin className="h-4 w-4" strokeWidth={2.25} />
          </span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500">
              {t('contacts.mapTitleShort')}
            </p>
            <p className="mt-1 font-display font-semibold text-ink-900 text-base md:text-[17px]">
              {address}
            </p>
            <p className="mt-1 text-sm text-ink-600 fx-text-pretty">
              {t('contacts.mapCaption')}
            </p>
          </div>
        </div>

        <div className="md:col-span-4 md:text-right">
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'group inline-flex items-center gap-2',
              'rounded-sm border-2 border-brand-500 px-5 py-3',
              'text-xs md:text-sm font-bold uppercase tracking-[0.14em] text-brand-600',
              'transition-colors duration-200',
              'hover:bg-brand-500 hover:text-white',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2'
            )}
          >
            {t('contacts.directionsCta')}
            <ArrowUpRight
              className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.75}
            />
          </a>
        </div>
      </div>
    </div>
  )
}

/* ============================================================
   ContactPage — the whole page composed together.
   ============================================================ */
export default function ContactPage() {
  const { t } = useTranslation()

  usePageSEO({
    title: t('meta.contacts.title'),
    description: t('meta.contacts.description'),
    ogTitle: t('meta.contacts.ogTitle'),
    ogDescription: t('meta.contacts.ogDescription'),
    ogImage: '/images/hero/industrial.jpg',
  })

  return (
    <main id="main-content" className="bg-ink-100">
      {/* ============================================================
          1 · HERO
          ============================================================ */}
      <PageHeader
        variant="dark"
        eyebrow={t('nav.contacts')}
        title={t('contacts.title')}
        subtitle={t('contacts.subtitle')}
      />

      {/* ============================================================
          2 · INTRO + MAIN (Contact record + Form)
          ============================================================ */}
      <Section tone="default" spacing="lg" width="default">
        {/* Short intro row */}
        <div className="mb-12 md:mb-14 max-w-3xl">
          <p className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-10 bg-brand-500" />
            {t('contacts.title')}
          </p>
          <p className="fx-text-pretty text-base md:text-lg leading-relaxed text-ink-600">
            {t('contacts.intro')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* LEFT — Contact record (5/12 on desktop) */}
          <div className="lg:col-span-5">
            <ContactRecordCard />
          </div>

          {/* RIGHT — Form (7/12 on desktop) */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </Section>

      {/* ============================================================
          3 · MAP BAND
          ============================================================ */}
      <Section tone="muted" spacing="lg" width="default">
        <div className="mb-10 md:mb-12 max-w-3xl">
          <p className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-10 bg-brand-500" />
            {t('contacts.mapEyebrow')}
          </p>
          <h2 className="fx-text-balance font-bold leading-[1.1] text-ink-900 text-2xl sm:text-3xl md:text-[2rem]">
            {t('contacts.mapTitle')}
          </h2>
        </div>

        <MapBlock />
      </Section>
    </main>
  )
}
